/* eslint-disable react/prefer-stateless-function */
import { Spinner, DateTimeInput } from '@schibsted-svp/react-ui';
import classnames from 'classnames/bind';
import { isEmpty, omit } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdCalendarMonth } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams, useLocation } from 'react-router-dom';
import config from 'config';
import { removeDuplicateLiveStreamInfo } from 'store/live/actions';
import { getDuplicatedAssetId, isLiveStreamDuplicating } from 'store/live/selectors';

import AppliedFilters from 'components/AssetsList/AppliedFilters';
import FiltersButton from 'components/AssetsList/FiltersButton';
import OrderDropdown from 'components/core/OrderDropdown';
import LiveAssetsList from 'components/LiveAssetsList/LiveAssetsList';
import ScreenContainer from 'components/ui/ScreenContainer';

import SearchForm from 'components/header/search/SearchForm';
import css from './LiveScreen2.module.scss';
import { useFiltersResizeObserver } from './hooks';

const cln = classnames.bind(css);

function LiveScreen2({
    isFetching = false,
    readyToPublishAssets = [],
    wasLiveAssets = [],
    currentLiveAssets = [],
    upcomingLiveAssets = [],
    personalLiveAssets = [],
    fetchLiveAssets,
    fetchLiveEncoders,
    fetchWasLiveAssets,
    newsroom,
    liveErrorMessage,
    wasLiveErrorMessage,
}) {
    const { tab = 'video' } = useParams();
    const location = useLocation();
    const history = useHistory();
    const searchQuery = new URLSearchParams(location.search).get('search') || '';
    const [searchTerm, setSearchTerm] = useState(searchQuery);
    const [selectedSort, setSelectedSort] = useState(config.sortTypes[0]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const tabsRef = useRef<HTMLUListElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);
    const filteredByRef = useRef<HTMLDivElement>(null);

    const selectedFilters = {
        categories: [
            { value: 1, label: 'Category Name' },
            { value: 2, label: 'Category Name 2' },
            { value: 3, label: 'Category Name 3' },
        ],
    };

    useEffect(() => {
        fetchLiveAssets?.({ provider: newsroom, assetType: tab });
    }, [fetchLiveAssets, newsroom, tab]);

    useEffect(() => {
        fetchLiveEncoders?.(newsroom);
    }, [fetchLiveEncoders, newsroom]);

    useEffect(() => {
        fetchWasLiveAssets?.({ provider: newsroom, assetType: tab });
    }, [fetchWasLiveAssets, newsroom, tab]);

    const filtersNaturalWidthRef = useRef(0);

    const { isFiltersWrapped } = useFiltersResizeObserver(
        containerRef,
        filteredByRef,
        tabsRef,
        searchRef,
        actionsRef,
        selectedFilters,
        searchQuery,
        filtersNaturalWidthRef
    );

    const isDuplicating = useSelector(isLiveStreamDuplicating);
    const duplicatedAssetId = useSelector(getDuplicatedAssetId);

    const dispatch = useDispatch();

    useEffect(() => {
        if (duplicatedAssetId && !isDuplicating) {
            history.push(`/${newsroom}/assets/${duplicatedAssetId}`);
            dispatch(removeDuplicateLiveStreamInfo());
        }
    }, [duplicatedAssetId, isDuplicating, dispatch, history, newsroom]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchTerm(value);
        },
        [setSearchTerm]
    );

    const handleSearchSubmit = () => {
        const params = new URLSearchParams();

        if (searchTerm) {
            params.append('query', searchTerm);
        } else {
            params.delete('query');
        }

        history.replace({
            pathname: location.pathname,
            search: params.toString(),
        });
    };

    if (isDuplicating) {
        return (
            <ScreenContainer>
                <Spinner containerClassName={css.spinner} />
                <p className={css.text}>Duplication in progress...</p>
            </ScreenContainer>
        );
    }

    if (isFetching) {
        return (
            <ScreenContainer>
                <Spinner containerClassName={css.spinner} />
            </ScreenContainer>
        );
    }

    const streamTypes = ['video', 'audio'];

    const onLiveTypeChange = (type) => {
        fetchLiveAssets({ provider: newsroom, assetType: type });
        fetchWasLiveAssets({ provider: newsroom, assetType: type });
    };

    return (
        <ScreenContainer>
            <div ref={containerRef} className={cln(css.filters, { [css.filtersWrapped]: isFiltersWrapped })}>
                <ul ref={tabsRef} className={css.streamTypesContainer}>
                    {streamTypes.map((type) => (
                        <NavLink to={`/${newsroom}/live/${type}`} key={type}>
                            <li
                                className={cln(css.streamTypeFilter, {
                                    'streamTypeFilter--active': tab === type,
                                })}
                                onClick={() => onLiveTypeChange(type)}
                            >
                                {type}
                            </li>
                        </NavLink>
                    ))}
                </ul>
                <div ref={searchRef} className={css.searchBox}>
                    <SearchForm
                        value={searchTerm}
                        onSubmit={handleSearchSubmit}
                        onChange={handleSearchChange}
                        size="small"
                    />
                </div>

                {(!isEmpty(omit(selectedFilters, ['statuses', 'customFields'])) || searchQuery) && (
                    <div ref={filteredByRef} className={css.filteredBy}>
                        <AppliedFilters
                            filters={omit(selectedFilters, ['statuses', 'customFields'])}
                            query={searchTerm}
                            onFiltersClear={() => {}}
                            onFilterDeletion={() => {}}
                            buttonSize="small"
                            nowrap={!isFiltersWrapped}
                        />
                    </div>
                )}

                <div ref={actionsRef} className={css.filtersActions}>
                    <div className={css.sortWrapper}>
                        <OrderDropdown
                            selectedSort={selectedSort}
                            onChange={setSelectedSort}
                            menuGap={35}
                            menuArrowPosition={105}
                        />
                    </div>
                    <div className={css.calendarWrapper}>
                        <DateTimeInput
                            value={selectedDate}
                            onChange={setSelectedDate}
                            icon={<MdCalendarMonth size={23} />}
                            iconOnly
                            isClearable
                            datePickerProps={{ dateFormat: 'dd.MM.yyyy' }}
                        />
                    </div>
                    <FiltersButton provider={newsroom} initialValues={selectedFilters} setFilters={() => {}} />
                </div>
            </div>
            {personalLiveAssets.length !== 0 && (
                <LiveAssetsList
                    header="My live videos"
                    visibleIcons={['date', 'views', 'duration', 'endTime']}
                    assets={personalLiveAssets}
                    errorMessage={liveErrorMessage}
                    fetchAssets={fetchLiveAssets}
                    newsroom={newsroom}
                    className={css['personal-lives']}
                />
            )}
            <div className={css['live-screen']}>
                <div className={css['live-and-was-live']}>
                    <div className={css['live-videos']}>
                        <LiveAssetsList
                            header="Current live videos"
                            visibleIcons={['date', 'views', 'duration', 'endTime']}
                            assets={currentLiveAssets}
                            errorMessage={liveErrorMessage}
                            fetchAssets={fetchLiveAssets}
                            newsroom={newsroom}
                            className={css['current-live']}
                        />
                        <LiveAssetsList
                            header="Upcoming live videos"
                            visibleIcons={['date', 'views', 'duration', 'endTime']}
                            assets={upcomingLiveAssets}
                            errorMessage={liveErrorMessage}
                            fetchAssets={fetchLiveAssets}
                            newsroom={newsroom}
                        />
                    </div>
                    <LiveAssetsList
                        header="Was live (last 24 hours)"
                        visibleIcons={['date', 'views', 'duration', 'endTime']}
                        assets={wasLiveAssets}
                        errorMessage={wasLiveErrorMessage}
                        fetchAssets={fetchWasLiveAssets}
                        newsroom={newsroom}
                    />
                </div>
                <LiveAssetsList
                    header="Ready to be published"
                    visibleIcons={[]}
                    assets={readyToPublishAssets}
                    errorMessage={liveErrorMessage}
                    fetchAssets={fetchLiveAssets}
                    newsroom={newsroom}
                />
            </div>
        </ScreenContainer>
    );
}

export default LiveScreen2;
